import { patchPost, postPost, putPostGoals, queryCollections } from '../api'
import { INFO_BLOCK_ROOT, PARAM_KEY_PAGE, PARAM_KEY_LIMIT, UNFINISHED_ROOT, PRIVACY_PUBLIC } from '../arch/constants'
import { emptyArray } from '../common'
import { SOURCE_PROPERTY } from '../constants'
import model from '../model'

import { ANALYZABLE_FILE_EXTENSIONS } from './constants'

const NEW_ID = 'new'

const displayedType = (typeRoot, fullType) =>
  typeRoot === INFO_BLOCK_ROOT ? 'Profile Update' : typeRoot === UNFINISHED_ROOT ? 'Draft' : fullType

const loadDocumentsPage = async (params, cursor = 0, limit) => {
  params.set(PARAM_KEY_LIMIT, limit)

  if (cursor) {
    params.set(PARAM_KEY_PAGE, cursor)
  }

  const collectionsPage = await queryCollections('documents', params)
  const { totalQueryCount, totalHouseholdCount, unfinishedCount, categories, posts } = collectionsPage
  const items = posts.slice(0, limit)
  const totalPages = Math.ceil(totalQueryCount / limit)

  return {
    // The counts are custom to documents pages due to the UI need to display them.
    totalQueryCount,
    totalHouseholdCount,
    unfinishedCount,
    categories,

    itemsPage: items.map(model.post),

    // There appears to be a bug in the API where a `cursor_next` is still returned even if the query result has
    // fewer items than the given limit, so we have to check that here.
    cursorNext: cursor != null && totalPages > cursor + 1 ? cursor + 1 : null
  }
}

// Transition: This is now a thin backward-compatibility wrapper on the new API.
const loadPostsPage = async (clientId, params, cursor, limit) => {
  params.set('childId', clientId)
  return await loadDocumentsPage(params, cursor, limit)
}

const emptyMedia = media => !media || media.length === 0

const savePostAndRelayChanges = async (payload, post, onChange) => {
  // Finalize the payload with some business logic.
  const { tags, referenceTimestamp } = payload
  const postPayload = {
    ...payload,
    inProgress: !tags || tags?.length === 0,
    referenceTimestamp: referenceTimestamp ? new Date(referenceTimestamp).toISOString() : null,
    id: post ? post.id : undefined
  }

  // For new posts, the API only accepts _missing_ client attributes, not falsy ones, to denote
  // a household-wide post.
  if (!postPayload.client && !post) {
    delete postPayload.client
  }

  const savedPost = await (post ? patchPost(postPayload) : postPost(postPayload))

  // Goals are saved with a separate call.
  const { goals } = postPayload
  const goalIds = goals?.map(goal => goal.id)
  if (goalIds) {
    await putPostGoals(savedPost.id, goalIds)
  }

  if (onChange) {
    onChange(savedPost)
  }
}

/**
 * Returns a placeholder title for a post. This seeks to be the filename that originated the post,
 * but has some fallbacks if that is not available.
 *
 * @param {object} post The provisional post whose title we want
 * @returns the title to use for the post
 */
const placeholderPostTitle = post => {
  const { id, assetDetails, media } = post

  const baseName = !emptyArray(assetDetails) ? assetDetails[0].fileName : media ? media[0] : ''
  return baseName || (media?.length > 0 ? 'Uploaded file' : `Document ID: ${id}`)
}

/**
 * Derives an initial post object based on an uploaded file (mediaId).
 *
 * @param {string} householdId The ID of the household
 * @param {string} clientId The ID of the child, if any
 * @param {array} media The array of asset IDs that originate the post
 * @param {object} assetsToNames Maps asset IDs to file names
 * @returns a placeholder post object
 */
const postFromMedia = (householdId, clientId, media, assetDetails) => ({
  householdId,
  client: clientId,
  type: UNFINISHED_ROOT,
  inProgress: true,
  media,
  assetDetails,
  privacy: PRIVACY_PUBLIC
})

/**
 * Builds placeholder post objects from a completed file-upload batch.
 *
 * @param {string} householdId The ID of the household
 * @param {string} clientId The ID of the child, if any
 * @param {array} batch Files to create posts from, object shape should be { file, mediaId }
 * @param {object} [options]
 * @param {boolean} [options.combineToOneDocument] If true, combines all files into a single post
 * @returns an array of placeholder post objects
 */
const postsFromUploadBatch = (householdId, clientId, batch, { combineToOneDocument } = {}) =>
  combineToOneDocument
    ? [
        postFromMedia(
          householdId,
          clientId,
          batch.map(entry => entry.mediaId),
          batch.map(entry => {
            const { file, mediaId: id } = entry
            return { fileName: file.name, id }
          })
        )
      ]
    : batch.map(entry => {
        const { file, mediaId: id } = entry
        return postFromMedia(householdId, clientId, [id], [{ fileName: file.name, id }])
      })

// Standardized empty array to avoid unnecessary re-renders.
const EMPTY_ARRAY = []

const valueOrBlank = value => value ?? ''
const valueOrEmptyArray = value => value ?? EMPTY_ARRAY

const postAssetDetails = post => valueOrEmptyArray(post?.assetDetails)
const postCategory = post => valueOrEmptyArray(post?.category)
const postDocumentType = post => valueOrEmptyArray(post?.documentType)
const postChildId = post => valueOrBlank(post?.client)
const postPrivacy = post => valueOrBlank(post?.privacy)
const postGoals = post => valueOrEmptyArray(post?.goals)
const postMedia = post => valueOrEmptyArray(post?.media)
const postName = post => valueOrBlank(post?.name)
const postNotes = post => valueOrBlank(post?.text)
const postSource = post => valueOrBlank((post?.additionalProperties ?? {})[SOURCE_PROPERTY])

// This one is more for convenience…it really just returns the _first_ filename of a post, so it won’t be accurate
// for multi-file posts but will be good enough most of the time.
const postFilename = post => {
  const { assetDetails } = post ?? {}
  const [firstAsset] = assetDetails ?? []
  return firstAsset?.fileName ?? ''
}

const updatePostState = ({
  postModel,
  setPost,
  setCategory,
  setDocumentType,
  setChildId,
  setPrivacy,
  setMedia,
  setAssetDetails,
  setName,
  setGoals,
  setNotes,
  setSource,
  setReferenceTimestamp
}) => {
  setPost(postModel)
  setCategory(postCategory(postModel))
  setDocumentType(postDocumentType(postModel))
  setChildId(postChildId(postModel))
  setPrivacy(postPrivacy(postModel))
  setMedia(postMedia(postModel))
  setAssetDetails(postAssetDetails(postModel))
  setName(postName(postModel))
  setGoals(postGoals(postModel))
  setNotes(postNotes(postModel))
  setSource(postSource(postModel))
  setReferenceTimestamp(postModel?.referenceTimestamp)
}

const postIsTagged = post => {
  const { tags } = post ?? {}
  return tags?.length > 0
}

/**
 * Finds a post’s priority as the list has it, for views (such as the detail) that must follow a change made there.
 *
 * @param {object[]} posts the listed posts
 * @param {string} postId the ID of the post to look up
 * @returns {boolean|undefined} whether the post is a priority, or `undefined` if it isn’t listed
 */
const listedPriorityOf = (posts, postId) => {
  const listedPost = posts?.find(post => post.id === postId)
  return listedPost ? Boolean(listedPost.priority) : undefined
}

const EXTENSION_REGEX = /\.([^.]+)$/
const fileExtension = fileName => fileName?.match(EXTENSION_REGEX)?.[1]?.toLowerCase() ?? ''

/**
 * A post can be analyzed/extracted only if it has at least one file and every one of its files
 * is a PDF or JPG.
 *
 * @param {object} post The post to check for PDF compatibility
 * @returns whether the post can be analyzed
 */
const postIsAnalyzable = post => {
  const assetDetails = post?.assetDetails ?? []
  return (
    assetDetails.length > 0 &&
    assetDetails.every(({ fileName, name }) => ANALYZABLE_FILE_EXTENSIONS.includes(fileExtension(fileName ?? name)))
  )
}

/**
 * Whether every file in a list can be analyzed/extracted.
 *
 * @param {array} files The files to check
 * @returns whether all files can be analyzed
 */
const canAnalyzeFiles = files => postIsAnalyzable({ assetDetails: Array.from(files) })

/**
 * Type tags follow a somewhat complex business rule: we eliminate any root type tags _unless_
 * all we have are root type tags 🥴
 *
 * @param {array} tags the array of tags from which to derive the type tag
 * @returns the array of tags which follows the type tag business rules
 */
const deriveTypeTags = tags => {
  const roots = tags.filter(tag => !tag.parentId)
  const nonRoots = tags.filter(tag => Boolean(tag.parentId))

  // When we have non-root tags, we return those plus any root tags that do _not_ have a child
  // among the non-roots.
  return nonRoots.length > 0
    ? tags.filter(tag => nonRoots.includes(tag) || !nonRoots.find(possibleChild => possibleChild.parentId === tag.id))
    : roots
}

const visibilityLabel = isPublicToTeam => `${isPublicToTeam ? 'Visible to' : 'Hidden from'} team`

export {
  NEW_ID,
  deriveTypeTags,
  displayedType,
  loadDocumentsPage,
  loadPostsPage,
  emptyMedia,
  savePostAndRelayChanges,
  placeholderPostTitle,
  postsFromUploadBatch,
  postAssetDetails,
  postCategory,
  postDocumentType,
  postChildId,
  postPrivacy,
  postGoals,
  postMedia,
  postName,
  postNotes,
  postSource,
  postFilename,
  postIsTagged,
  listedPriorityOf,
  fileExtension,
  postIsAnalyzable,
  canAnalyzeFiles,
  updatePostState,
  visibilityLabel
}
